import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Workflow, X, LoaderCircle } from 'lucide-react'

import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import FolderPicker from '../detail/FolderPicker'
import WorkflowFlowEditor, {
  WorkflowFlowMember,
  WorkflowStepDraft,
  draftsFromPlan,
  draftsToInput,
  findFolderName,
  templateShapeHasIssues,
} from './workflow-flow'

interface WorkflowTemplateDialogProps {
  projectId: string
  /** The template being edited, or null for a brand-new one. */
  template: any | null
  members: WorkflowFlowMember[]
  onClose: () => void
  onSaved: () => void
}

/**
 * Create/edit one workflow template. It is its own wide dialog rather than a
 * column inside SubmittalLookupsDialog's `sm:max-w-2xl` shell, because the flow
 * canvas needs the width — the lookups dialog keeps only the template LIST.
 *
 * Mounted only while a template row (or "+ New template") was clicked, so every
 * open starts from the selected template's own values without any reset logic.
 */
const WorkflowTemplateDialog: React.FC<WorkflowTemplateDialogProps> = ({
  projectId,
  template,
  members,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation()
  const [name, setName] = useState<string>(template?.name || '')
  const [targetFolderId, setTargetFolderId] = useState<string | null>(template?.targetFolderId || null)
  const [steps, setSteps] = useState<WorkflowStepDraft[]>(() =>
    draftsFromPlan(
      (template?.steps || []).map((s: any) => ({
        name: s.name,
        durationDays: s.durationDays,
        reviewerIds: (s.reviewers || []).map((r: any) => r.id),
      })),
    ),
  )

  // Same query FolderPicker itself runs, so this is an Apollo cache hit rather
  // than a second round trip — it only exists to turn the stored folder id into
  // the name the Publish node displays.
  const { data: foldersData } = useQuery(Query.DOC_FOLDER_TREE, {
    variables: { projectId },
    skip: !projectId,
  })
  const targetFolderName = findFolderName(foldersData?.docFolderTree, targetFolderId)

  const [upsertTemplate, { loading: saving }] = useMutation(Mutation.UPSERT_SUBMITTAL_WORKFLOW_TEMPLATE)

  // Shape only — steps may be unstaffed, a template is a skeleton (#31947103).
  const stepsInvalid = templateShapeHasIssues(steps)

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed || stepsInvalid) return
    try {
      await upsertTemplate({
        variables: {
          input: {
            id: template?.id || undefined,
            projectId,
            name: trimmed,
            targetFolderId: targetFolderId || null,
            steps: draftsToInput(steps),
          },
        },
      })
      toastr.success('', t('Submittals_wf_saved'))
      onSaved()
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    }
  }

  const publishPanel = (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label>{t('Submittals_wf_target_folder')}</Label>
        {targetFolderId && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setTargetFolderId(null)}
            title={t('Common_clear')}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <FolderPicker projectId={projectId} value={targetFolderId} onChange={setTargetFolderId} />
    </div>
  )

  return (
    <Dialog open onOpenChange={(next) => { if (!next) handleClose() }}>
      <DialogContent className="sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Workflow className="h-5 w-5 text-primary" />
            {t(template ? 'Submittals_wf_edit_template' : 'Submittals_wf_new_template')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="wf-template-name">{t('Submittals_wf_template_name')}</Label>
            <Input
              id="wf-template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={cn('h-9', !name.trim() && 'border-destructive')}
            />
          </div>

          <WorkflowFlowEditor
            steps={steps}
            onChange={setSteps}
            members={members}
            publishFolderName={targetFolderName}
            publishPanel={publishPanel}
            requireReviewers={false}
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={saving}>
            {t('Common_cancel')}
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving || !name.trim() || stepsInvalid}>
            {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Common_save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default WorkflowTemplateDialog
